import { useState } from "react";
import {
	DEFAULT_BAND_FACTOR,
	isInBand,
	medianClaimedPop,
	type MpMember,
} from "../../../common/multiplayerConfig.ts";
import type { View } from "../../../common/types.ts";
import { ActionButton } from "../../components/ActionButton.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { confirm } from "../../util/confirm.tsx";
import { logEvent } from "../../util/logEvent.ts";
import { realtimeUpdate } from "../../util/realtimeUpdate.ts";
import { toWorker } from "../../util/toWorker.ts";

const showError = (error: unknown) => {
	logEvent({
		type: "error",
		text: error instanceof Error ? error.message : String(error),
		saveToDb: false,
	});
};

const MemberRow = ({
	member,
	teamName,
	isMe,
}: {
	member: MpMember;
	teamName: string | undefined;
	isMe: boolean;
}) => {
	return (
		<li className="list-group-item d-flex align-items-center gap-2">
			<span className={isMe ? "fw-bold" : undefined}>
				{member.display_name}
			</span>
			{member.role === "commissioner" ? (
				<span className="badge text-bg-secondary">Commissioner</span>
			) : null}
			{member.out_of_band ? (
				<span
					className="badge text-bg-warning"
					title="This team is outside the market-size band of the other claimed teams"
				>
					Outside band
				</span>
			) : null}
			<span className="ms-auto text-body-secondary">
				{teamName ?? <i>No team claimed</i>}
			</span>
		</li>
	);
};

const MultiplayerLobby = ({
	leagueId,
	session,
	league,
	members,
	teams,
	localLid,
	mpError,
}: View<"multiplayerLobby">) => {
	useTitleBar({
		title: league ? `Lobby: ${league.name}` : "Multiplayer",
	});

	const [processing, setProcessing] = useState(false);

	if (!session) {
		return (
			<div className="alert alert-warning d-inline-block">
				You are not signed in. Go to <a href="/multiplayer">Multiplayer</a> to
				sign in first.
			</div>
		);
	}

	if (mpError) {
		return (
			<div className="alert alert-danger d-inline-block">
				Could not load this lobby: {mpError}
			</div>
		);
	}

	if (!league) {
		return null;
	}

	const me = members.find((member) => member.user_id === session.userId);
	const isCommissioner = league.commissioner === session.userId;
	const bandFactor = league.settings.bandFactor ?? DEFAULT_BAND_FACTOR;

	// Same definition as the server's claim_team RPC: median over the teams
	// claimed by everyone but me.
	const medianPop = medianClaimedPop(members, teams, session.userId);

	const teamName = (tid: number | null) => {
		if (tid === null) {
			return undefined;
		}
		const t = teams.find((t) => t.tid === tid);
		return t ? `${t.region} ${t.name}` : undefined;
	};

	const claim = async (tid: number, confirmOutOfBand: boolean = false) => {
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpClaimTeam", {
				leagueId,
				tid,
				confirmOutOfBand,
			});
			if (result.status === "out_of_band") {
				const proceed = await confirm(
					`This team's market size (${result.pop} million people) is far from the median of the teams your friends claimed (${result.median} million). Big markets earn more money and small markets less, so this can be unfair. Claim it anyway?`,
					{
						okText: "Claim Anyway",
						cancelText: "Cancel",
					},
				);
				if (proceed) {
					await claim(tid, true);
				}
			} else if (result.status === "taken") {
				showError(new Error("Someone else just claimed that team."));
			} else if (result.status === "already_started") {
				showError(new Error("This league has already started."));
			}
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const startLeague = async () => {
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpStartLeague", leagueId);
			if (result.status === "unclaimed_members") {
				showError(
					new Error(
						`${result.count} member(s) have not claimed a team yet. Everyone needs a team before the league can start.`,
					),
				);
			} else {
				await realtimeUpdate([], `/l/${result.lid}`);
			}
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const joinStartedLeague = async () => {
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpJoinStartedLeague", leagueId);
			await realtimeUpdate([], `/l/${result.lid}`);
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const claimingOpen = league.status === "lobby";

	return (
		<>
			<p>
				Invite code: <code className="fs-5">{league.invite_code}</code>
				<span className="text-body-secondary">
					{" "}
					- share it with friends so they can join
				</span>
			</p>
			<p className="text-body-secondary">
				{league.settings.rosters === "real" ? (
					<>
						Real players, starting in <b>{league.settings.startingSeason}</b>.
					</>
				) : (
					"Random players."
				)}{" "}
				All players get redrafted when the league starts, so nobody inherits a
				stacked roster.
			</p>

			<div className="row gy-3">
				<div className="col-md-5 col-lg-4">
					<h2>Players</h2>
					<ul className="list-group mb-3">
						{members.map((member) => (
							<MemberRow
								key={member.user_id}
								member={member}
								teamName={teamName(member.tid)}
								isMe={member.user_id === session.userId}
							/>
						))}
					</ul>

					{league.status === "lobby" && isCommissioner ? (
						<>
							<ActionButton
								size="lg"
								processing={processing}
								processingText="Generating league"
								onClick={startLeague}
							>
								Start League
							</ActionButton>
							<p className="text-body-secondary mt-2">
								Starting runs a full redraft (all players get scrambled across
								all teams, so nobody inherits a stacked roster), then uploads
								the league for everyone else to download.
							</p>
						</>
					) : null}

					{league.status === "lobby" && !isCommissioner ? (
						<p className="text-body-secondary">
							Waiting for the commissioner to start the league.
						</p>
					) : null}

					{league.status === "starting" && !isCommissioner ? (
						<p>
							<span
								className="spinner-border spinner-border-sm me-1"
								role="status"
							/>
							The commissioner is generating the league...
						</p>
					) : null}

					{league.status === "starting" && isCommissioner ? (
						<ActionButton
							size="lg"
							processing={processing}
							processingText="Generating league"
							onClick={startLeague}
						>
							Resume League Creation
						</ActionButton>
					) : null}

					{league.status === "active" ? (
						localLid !== undefined ? (
							<a className="btn btn-lg btn-primary" href={`/l/${localLid}`}>
								Open League
							</a>
						) : (
							<ActionButton
								size="lg"
								processing={processing}
								processingText="Downloading league"
								onClick={joinStartedLeague}
							>
								Download & Join League
							</ActionButton>
						)
					) : null}
				</div>

				<div className="col-md-7 col-lg-6">
					<h2>Teams</h2>
					<p className="text-body-secondary">
						Pick any team whose market size is close to your friends' teams.
						Teams outside the band (grayed out) can still be claimed, with a
						warning.
					</p>
					<div className="table-responsive">
						<table className="table table-striped table-hover align-middle">
							<thead>
								<tr>
									<th>Team</th>
									<th className="text-end">Market size</th>
									<th>Claimed by</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{teams.map((t) => {
									const claimedBy = members.find(
										(member) => member.tid === t.tid,
									);
									const inBand = isInBand(t.pop, medianPop, bandFactor);
									const claimedByMe = claimedBy?.user_id === session.userId;
									return (
										<tr
											key={t.tid}
											className={!inBand ? "text-body-secondary" : undefined}
										>
											<td>
												{t.region} {t.name}
												{!inBand ? (
													<span
														className="badge text-bg-light ms-2"
														title={`Outside the market-size band (within ${bandFactor}x of the median claimed team)`}
													>
														Outside band
													</span>
												) : null}
											</td>
											<td className="text-end">{t.pop}M</td>
											<td>
												{claimedBy ? (
													<span className={claimedByMe ? "fw-bold" : undefined}>
														{claimedBy.display_name}
													</span>
												) : null}
											</td>
											<td>
												{claimingOpen && !claimedBy ? (
													<button
														className="btn btn-sm btn-light-bordered"
														disabled={processing || claimedByMe}
														onClick={() => claim(t.tid)}
													>
														Claim
													</button>
												) : null}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{me && claimingOpen ? (
						<p>
							{me.tid === null ? (
								"You have not claimed a team yet."
							) : (
								<>
									Your team: <b>{teamName(me.tid)}</b> (claim a different team
									to switch)
								</>
							)}
						</p>
					) : null}
				</div>
			</div>
		</>
	);
};

export default MultiplayerLobby;
